"use client";

import React from "react";
import BookingPage from "./BookingPage";
import { EditorialBookingPageRoot } from "./EditorialBookingPageRoot";
import type { BookingVisualVariantKey } from "./bookingVisualVariants";
import { BookingProvider, useBookingContext } from "../Booking/BookingContext";
import type { BookingData } from "../Booking/bookingData";
import type { BookingInfoBlog } from "./BookingInfoPanel";

// ---------------------------------------------------------------------------
// Props — the booking page frame plus serializable booking data
// ---------------------------------------------------------------------------

type LiveBookingPageProps = {
  salon: { name: string; logoUrl?: string };
  data?: Record<string, any>;
  bookingData: BookingData;
  mode?: "live" | "preview";
  visualVariant?: BookingVisualVariantKey;
  memberPageBaseHref?: string;
  homeHref?: string;
  /** Info drawer Blog row; omitted when the blog is off or empty. */
  blog?: BookingInfoBlog | null;
};

// ---------------------------------------------------------------------------
// Inner component that consumes booking context
// ---------------------------------------------------------------------------

function BookingPageWithContext({
  salon,
  data,
  mode,
  homeHref,
  blog,
}: Omit<LiveBookingPageProps, "bookingData">) {
  const { booking } = useBookingContext();
  return (
    <BookingPage
      route="/"
      salon={salon}
      data={data}
      booking={booking}
      mode={mode}
      homeHref={homeHref}
      blog={blog}
    />
  );
}

// ---------------------------------------------------------------------------
// LiveBookingPage — wraps BookingPage with BookingProvider
// ---------------------------------------------------------------------------

export function LiveBookingPage({
  salon,
  data,
  bookingData,
  mode = "live",
  visualVariant,
  memberPageBaseHref,
  homeHref,
  blog,
}: LiveBookingPageProps) {
  return (
    <EditorialBookingPageRoot visualVariant={visualVariant}>
      <BookingProvider
        bookingData={bookingData}
        memberPageBaseHref={memberPageBaseHref}
      >
        <BookingPageWithContext
          salon={salon}
          data={data}
          mode={mode}
          homeHref={homeHref}
          blog={blog}
        />
      </BookingProvider>
    </EditorialBookingPageRoot>
  );
}
